import React from 'react';
import { useNavigate } from 'react-router-dom';
import { LockClosedIcon } from '@heroicons/react/24/solid';
import {
  formatSpecialisation,
  truncateText,
} from '../../utils/advisorNetworkConstants';

const VerifiedBadge = ({ credentialType }) => (
  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-pine text-bone-raised text-label font-medium">
    <LockClosedIcon className="w-3 h-3" aria-hidden="true" />
    Verified {credentialType}
  </span>
);

const AdvisorCard = ({
  advisor,
  compact = false,
  following = false,
  followLoading = false,
  onFollow,
  showFollow = true,
}) => {
  const navigate = useNavigate();

  const handleCardClick = () => {
    navigate(`/advisor-network/${advisor.advisor_id}`);
  };

  const handleFollowClick = (event) => {
    event.stopPropagation();
    if (onFollow) onFollow(advisor.advisor_id);
  };

  const specialisations = advisor.specialisations || [];
  const topSpec = specialisations[0];

  return (
    <article
      className="aura-card cursor-pointer transition-colors duration-300 hover:bg-pine-tint/40"
      onClick={handleCardClick}
      onKeyDown={(event) => {
        if (event.key === 'Enter') handleCardClick();
      }}
      role="button"
      tabIndex={0}
    >
      <div className="flex items-start justify-between gap-4 mb-3">
        <div className="min-w-0 flex-1">
          <h3 className="text-body font-medium text-ink truncate">{advisor.full_name}</h3>
          <div className="mt-2">
            <VerifiedBadge credentialType={advisor.credential_type} />
          </div>
        </div>
        {showFollow && (
          <button
            type="button"
            onClick={handleFollowClick}
            disabled={followLoading || following}
            className={`shrink-0 px-3 py-1.5 rounded-md text-label font-medium border transition-colors duration-300 ${
              following
                ? 'bg-pine text-bone-raised border-pine'
                : 'bg-transparent text-pine border-pine hover:bg-pine-tint'
            }`}
          >
            {following ? 'Following' : 'Follow'}
          </button>
        )}
      </div>

      {!compact && specialisations.length > 0 && (
        <p className="text-label text-ink-muted mb-2">
          {specialisations.map((spec) => formatSpecialisation(spec)).join(', ')}
        </p>
      )}

      {compact && topSpec && (
        <p className="text-label text-ink-muted mb-2">{formatSpecialisation(topSpec)}</p>
      )}

      {!compact && advisor.bio && (
        <p className="text-body text-ink mb-2">{truncateText(advisor.bio, 100)}</p>
      )}

      <p className="text-label text-ink-muted mb-2">
        {(advisor.follower_count ?? 0).toLocaleString()} followers
      </p>

      {!compact && advisor.latest_post && (
        <p className="text-body text-ink-muted italic">{truncateText(advisor.latest_post, 100)}</p>
      )}
    </article>
  );
};

export default AdvisorCard;
